import { ElementType, FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

type SessionStatsCardKindType = "sessions" | "attendance" | "student_talk_time" | "engagement";

type SessionStatsCardProps = {
  kind: SessionStatsCardKindType;
  /** Title text displayed in the header */
  title: string;
  /** Icon displayed in the header (top right) */
  TitleIcon: ElementType;
  /** Main metric value displayed prominently */
  value: number | string;
  /** Description text displayed next to the value */
  valueDescription: string;
  /** Icon displayed in the footer */
  FooterIcon: ElementType;
  /** Footer text content */
  footerText: ReactNode;
  /** Additional className for the card container */
  className?: string;
  /** Whether the card is empty */
  isEmpty?: boolean;
};

const getColorsForKind = (kind: SessionStatsCardKindType, isEmpty?: boolean) => {
  if (isEmpty) {
    return {
      backgroundColor: "bg-black-tint-97",
      accentColor: "text-black-tint-55",
      separatorColor: "bg-black-tint-90",
    };
  }
  if (kind === "sessions") {
    return {
      backgroundColor: "bg-orange-tint-93",
      accentColor: "text-orange-shade-20",
      separatorColor: "bg-orange-tint-70",
    };
  }
  if (kind === "attendance") {
    return {
      backgroundColor: "bg-blue-tint-95",
      accentColor: "text-blue",
      separatorColor: "bg-blue-tint-70",
    };
  }
  if (kind === "engagement") {
    return {
      backgroundColor: "bg-[#F0F9F5]",
      accentColor: "text-green-shade-20",
      separatorColor: "bg-green-tint-70",
    };
  }
  return {
    backgroundColor: "bg-hibiscus-tint-95",
    accentColor: "text-hibiscus",
    separatorColor: "bg-hibiscus-tint-70",
  };
};

export const SessionStatsCard: FC<SessionStatsCardProps> = ({
  kind,
  title,
  TitleIcon,
  value,
  valueDescription,
  FooterIcon,
  footerText,
  className,
  isEmpty,
}) => {
  const { backgroundColor, accentColor, separatorColor } = getColorsForKind(kind, isEmpty);
  return (
    <div
      className={twMerge(
        "flex flex-col gap-4 rounded-xl px-[1.125rem] py-4",
        backgroundColor,
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className={twMerge("text-lg font-medium", accentColor)}>{title}</h3>
        <div className={accentColor}>
          <TitleIcon height={24} width={24} />
        </div>
      </div>
      {/* Main Metric */}
      <div className="grid grid-cols-[auto_1fr] items-end gap-3">
        <span className={twMerge("text-4xl font-medium leading-none", accentColor)}>
          {isEmpty ? "-" : value}
        </span>
        <div className="flex min-h-[2rem] flex-col justify-end pb-[0.125rem]">
          <span className="text-xs font-normal leading-4 text-black-tint-40">
            {/* Use non-breaking space (\u00A0) instead of empty string to ensure min-h-[2rem] is respected when isEmpty */}
            {isEmpty ? "\u00A0" : valueDescription}
          </span>
        </div>
      </div>

      {/* Separator */}
      <div className={twMerge("h-px w-full", separatorColor)} />

      {/* Footer */}
      <div className="flex items-center gap-2">
        <div className={accentColor}>
          <FooterIcon />
        </div>
        <span className="text-xs font-normal text-black-tint-40">{footerText}</span>
      </div>
    </div>
  );
};
